import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { SpaceInterface } from "@/resources/interfaces/spaceInterfaces";
import Pusher from "pusher-js";
import { Socket } from "@/resources/constants/socket";
import { mixins } from "vue-class-component";
import Loader, { CANCELLATION_TIME } from "@/resources/mixins/loader/loader";
import ConsoleMessage from "@/resources/mixins/notifications/consoleMessage";
import { SpaceUserInterface } from "@/resources/interfaces/spaceUserInterfaces";
import NotificationEvent from "@/resources/mixins/notifications/notificationEvent";
import { i18n } from "@/resources/locales";
import PaperPlane from "@/resources/mixins/notifications/paperPlane";
import SpaceUserSocketHandler from "@/helpers/socket/handlers/spaceUserSocket.handler";
import UserSocketHandler from "@/helpers/socket/handlers/userSocket.handler";
import SpaceSocketHandler from "@/helpers/socket/handlers/spaceSocket.handler";
import ScenarioSocketHandler from "@/helpers/socket/handlers/scenarioSocket.handler";
import RoomSocketHandler from "@/helpers/socket/handlers/roomSocket.handler";
import { AuthService } from "@/services/auth/auth.service";

import { PUSHER_CHANNEL, PUSHER_STATES } from "@/resources/constants/pusher";
import moment from "moment";
import { MEETING_TYPE } from "@/resources/constants/meeting";
import UserValidator from "../../resources/mixins/userValidator";
import GraphicClient from "@/resources/mixins/graphicClient/graphicClient";
import { MessageType, UserSendMessageType } from "@/resources/constants/graphicClient/graphicClient";
import Plans from "@/resources/mixins/plans/plans";
import { PLAN } from "@/resources/constants/plan";

@Component({})
export default class PusherSocket extends mixins(
  Loader,
  ConsoleMessage,
  NotificationEvent,
  PaperPlane,
  UserValidator,
  Plans
) {
  @Getter("SpaceStore/getSpace") space!: SpaceInterface;
  @Getter("UserStore/getSpaceUser") spaceUser!: SpaceUserInterface;
  isUnavailable: string;
  connectionStarted: boolean;
  firstTime = false;
  randomString = null;
  timer = null;
  mounted(): void {
    this.randomString = (Math.random() + 1).toString(36).substring(2);
  }
  setConnectionStarted(connectionStarted: boolean): void {
    this.connectionStarted = connectionStarted;
  }
  getPusherStatus(): string | null {
    const pusher = this.$store.getters["SystemStore/getPusher"];
    if (pusher) return pusher.connection.state;
    return null;
  }
  async createPusher(): Promise<any> {
    if (process.env.VUE_APP_ENVIRONMENT !== "PRO") console.log("createPusher");
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const authService = new AuthService();
      await authService.refreshToken();
      const token = `Bearer ${localStorage.getItem("token")}`;
      let pusher = this.$store.getters["SystemStore/getPusher"];
      if (pusher) {
        pusher.connection.disconnect();
      }
      pusher = await new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        authEndpoint: `${process.env.VUE_APP_API_BASE}pusher/authentication`,
        auth: { params: {}, headers: { Authorization: token } },
      });
      await this.$store.dispatch("SystemStore/setPusherAction", pusher);
      if (pusher == undefined) reject(false);
      // CHECK ERROR
      pusher.connection.bind("error", async (error) => {
        if (process.env.VUE_APP_ENVIRONMENT !== "PRO") {
          console.log("PUSHER ERROR");
          this.onError("PUSHER CONNECTION ERROR", JSON.stringify(error));
        }
        this.isUnavailable = PUSHER_STATES.UNAVAILABLE;
        reject(false);
      });
      // CHANGE STATE
      pusher.connection.bind("state_change", async (states) => {
        console.log("PUSHER STATE CHANGE", JSON.stringify(states));
        if (
          states.current.toLowerCase() ==
          PUSHER_STATES.UNAVAILABLE.toLowerCase()
        ) {
          this.isUnavailable = states.current;
          //window.location.reload();
        }
        if (states.current == "failed") {
          if (process.env.VUE_APP_ENVIRONMENT !== "PRO")
            this.onError("", i18n.t("notifications.errorMessages.pusherError"));
        }
      });
      // CONNECTED
      pusher.connection.bind("connected", async () => {
        console.log("connected ", this.firstTime);
        if (
          this.isUnavailable != undefined &&
          this.isUnavailable.toLowerCase() ==
          PUSHER_STATES.UNAVAILABLE.toLowerCase()
        ) {
          console.log("Connectado a los canales");
          this.isUnavailable = "";
          this.subscribeChannels();
        } else if (this.firstTime) {
          await this.getOnlyUserOnline();
        }
        this.firstTime = true;
        resolve(true);
      });
    });
  }
  async subscribe(): Promise<any> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const pusher = this.$store.getters["SystemStore/getPusher"];
      if (!pusher) {
        reject(false);
      } else if (
        pusher.connection.state === "connected" &&
        this.connectionStarted
      ) {
        await this.subscribeToSpaceUserPrivateChannel();
        await this.subscribeToSpace();
        await this.subscribeToUserPrivateChannel();
        resolve(true);
      }
    });
  }
  async subscribeChannels(): Promise<void> {
    await this.checkMeeting();
    if (process.env.VUE_APP_ENVIRONMENT !== "PRO")
      console.log("PUSHER RECONNECT subscribeChannels");
    //Todo refrescamos el token
    const pusher = this.$store.getters["SystemStore/getPusher"];
    const authService = new AuthService();
    await authService.refreshToken().then(async () => {
      pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
        "token"
      )}`;
    });
    await this.$store.dispatch("SystemStore/setPusherAction", pusher);
    await this.subscribeToSystem();
    await this.subscribeToSpaceUserPrivateChannel();
    await this.subscribeToSpace();
    await this.subscribeToUserPrivateChannel();
    await this.getOnlyUserOnline();
  }
  async subscribeToSystem(): Promise<void> {
    const pusher = this.$store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(PUSHER_CHANNEL.PUBLIC_SYSTEM);
    if (channelExist && channelExist.subscribe) return;
    if (pusher.connection.state === "connected") {
      const channel = pusher.subscribe(PUSHER_CHANNEL.PUBLIC_SYSTEM);
      channel.bind(Socket.MAINTENANCE_ON, async (data) => {
        await SpaceSocketHandler.maintenanceOn(this.$store, data);
      });
      channel.bind(Socket.MAINTENANCE_OFF, async (data) => {
        await SpaceSocketHandler.maintenanceOff(this.$store, data);
      });
      channel.bind(Socket.EXTEND_MAINTENANCE, async (data) => {
        await SpaceSocketHandler.maintenanceExtend(this.$store, data);
      });
      channel.bind(Socket.REFRESH_BROWSER, async () => {
        window.location.reload();
      });
      channel.bind(Socket.NEXT_MAINTENANCE, async (data) => {
        await SpaceSocketHandler.nextMaintenance(this.$store, data);
      });
      channel.bind(Socket.CANCEL_NEXT_MAINTENANCE, async () => {
        await SpaceSocketHandler.cancelNextMaintenance(this.$store);
      });
      channel.bind(Socket.GLOBAL_PARAMS_UPDATED, async (data) => {
        await SpaceSocketHandler.globalParamsUpdated(this.$store, data);
      });
    }
  }
  async subscribeToSpaceUserPrivateChannel(): Promise<void> {
    const pusher = this.$store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(
      `${PUSHER_CHANNEL.PRIVATE_SPACE_USER}${this.spaceUser.id}_${this.randomString}`
    );
    if (channelExist && channelExist.subscribe) return;
    if (pusher.connection.state === "connected") {
      let channel = pusher.subscribe(
        `${PUSHER_CHANNEL.PRIVATE_SPACE_USER}${this.spaceUser.id}_${this.randomString}`
      );
      channel.bind("pusher:subscription_error", async (status) => {
        console.log(
          "subscribe to spaceUser private channel error ",
          moment().toString(), " ", status
        );
        if (status == 403){
          this.onWarning("", i18n.t("notifications.descriptions.spaceFull"));
          let pusher = this.$store.getters["SystemStore/getPusher"];
          if (pusher) {
            pusher.connection.disconnect();
          }
          setTimeout(() => {
            localStorage.removeItem("space_default");
            localStorage.setItem("city", "ON");
            window.location.reload();
          }, 2000);
        }
        if (status == 401) {
          channel.subscriptionPending = false;
          const authService = new AuthService();
          await authService.refreshToken(true).then(async () => {
            pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
              "token"
            )}`;
            await this.$store.dispatch("SystemStore/setPusherAction", pusher);
            channel = pusher.subscribe(
              `${PUSHER_CHANNEL.PRIVATE_SPACE_USER}${this.spaceUser.id}_${this.randomString}`
            );
          });
        } /* else {
          if (localStorage.getItem("WEB_CLIENT_LOG_ACTIVATED") == "1")
            this.$store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: "Pusher subscribe to spaceUser private channel error " + status,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });
        }*/
      });
    }
  }
  async subscribeToSpace(): Promise<void> {
    if (this.space == undefined || this.space.id == undefined) {
      setTimeout(() => {
        this.subscribeToSpace();
      }, 10000);
      return;
    }
    const pusher = this.$store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(
      PUSHER_CHANNEL.PRIVATE_SPACE + this.space.id
    );

    if (channelExist && channelExist.subscribe) return;
    if (pusher.connection.state === "connected") {
      let channel = pusher.subscribe(
        PUSHER_CHANNEL.PRIVATE_SPACE + this.space.id
      );
      channel.bind("pusher:subscription_error", async (status) => {
        console.log("subscribe to space channel error ", moment().toString(), " ", status);
        if (status == 401) {
          channel.subscriptionPending = false;
          const authService = new AuthService();
          await authService.refreshToken(true).then(async () => {
            pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
              "token"
            )}`;
            await this.$store.dispatch("SystemStore/setPusherAction", pusher);
            channel = pusher.subscribe(
              PUSHER_CHANNEL.PRIVATE_SPACE + this.space.id
            );
          });
        } /* else {
          if (localStorage.getItem("WEB_CLIENT_LOG_ACTIVATED") == "1")
            this.$store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: "Pusher subscribe to space channel error " + status,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });
        }*/
      });
      channel.bind(Socket.USER_CONNECTED, async (data) => {
        await UserSocketHandler.userConnected(this.$store, data.data);
      });
      channel.bind(Socket.USER_DELETED, async (data) => {
        await UserSocketHandler.userDeleted(this.$store, data.data);
      });
      channel.bind(Socket.USER_DISCONNECTED, async (data) => {
        await UserSocketHandler.userDisconnected(this.$store, data.data, this.connectGenetalChat());
      });
      channel.bind(Socket.USER_PROFILE_EDITED, async (data) => {
        await UserSocketHandler.userProfileEdited(this.$store, data.data);
      });
      channel.bind(Socket.USER_AVATAR_CHANGED, async (data) => {
        await UserSocketHandler.userAvatarChanged(this.$store, data.data);
      });
      channel.bind(Socket.USER_PET_CHANGED, async (data) => {
        await UserSocketHandler.userAvatarChanged(this.$store, data.data);
      });
      channel.bind(Socket.USER_STATUS_CHANGED, async (data) => {
        await UserSocketHandler.userStatusChanged(this.$store, data.data);
      });
      channel.bind(Socket.USER_STATUS_VACATION_ON, async (data) => {
        await UserSocketHandler.userStatusVacationOn(this.$store, data.data);
      });
      channel.bind(Socket.USER_STATUS_VACATION_OFF, async (data) => {
        await UserSocketHandler.userStatusVacationOff(this.$store, data.data);
      });
      channel.bind(Socket.USER_WORK_IN_OFFICE_CHANGED, async (data) => {
        await UserSocketHandler.userWorkInOfficeChanged(this.$store, data.data);
      });
      channel.bind(Socket.USER_DISABLED, async (data) => {
        await UserSocketHandler.userDisabled(
          this.$store,
          data.data,
          this.$router
        );
      });
      channel.bind(Socket.USER_PROFILE_IMAGE_UPDATED, async (data) => {
        await UserSocketHandler.userProfileImageUpdated(this.$store, data.data);
      });
      channel.bind(Socket.SPACE_USER_DISABLED, async (data) => {
        await UserSocketHandler.userDisabled(
          this.$store,
          data.data,
          this.$router
        );
      });
      channel.bind(Socket.SPACE_DISABLED, async (data) => {
        await SpaceSocketHandler.spaceDisabled(this.$store, data.data);
      });
      channel.bind(Socket.SPACE_SCENARIO_CHANGED, async (data) => {
        await SpaceSocketHandler.spaceScenarioChanged(this.$store, data.data);
      });
      channel.bind(Socket.SCENARIO_ROOMS_EDITED, async (data) => {
        await ScenarioSocketHandler.scenarioRoomsEdited(this.$store, data.data);
      });
      channel.bind(Socket.ROOM_IMAGE_UPDATED, async (data) => {
        await RoomSocketHandler.roomImageUpdated(this.$store, data.data);
      });
      channel.bind(Socket.SPACE_SPACE_EDITED, async (data) => {
        await SpaceSocketHandler.spaceChanged(this.$store, data.data);
      });
      channel.bind(Socket.SPACE_USER_PERMISSIONS_CHANGED, async (data) => {
        await SpaceUserSocketHandler.spaceUserPermissionsChanged(
          this.$store,
          data.data,
          this.startLoader,
          this.setLoaderText
        );
      });
      channel.bind(Socket.SPACE_USER_DELETED, async (data) => {
        await SpaceUserSocketHandler.spaceUserDelete(this.$store, data.data);
      });
      channel.bind(Socket.SUPER_SPACE_USER_DELETED, async (data) => {
        await SpaceUserSocketHandler.superSpaceUserDeleted(
          this.$store,
          data.data
        );
      });

      channel.bind(Socket.SCENARIO_IMAGES_UPDATED, async (data) => {
        await ScenarioSocketHandler.scenarioImagesUpdated(
          this.$store,
          data.data
        );
      });
      channel.bind(Socket.SPACE_IMAGE_UPDATED, async (data) => {
        await SpaceSocketHandler.spaceImageUpdate(this.$store, data.data);
      });

      channel.bind(Socket.ROOM_WEBINAR_OPENED, async (data) => {
        await RoomSocketHandler.roomWebinarOpened(
          this.$store,
          data.data,
          this.$vs
        );
      });
      channel.bind(Socket.ROOM_WEBINAR_CLOSED, async (data) => {
        await RoomSocketHandler.roomWebinarClosed(
          this.$store,
          data.data,
          this.$vs
        );
      });
      channel.bind(Socket.ROOM_VIDEO_URL_CHANGED, async (data) => {
        await RoomSocketHandler.roomVideoUrlChanged(this.$store, data.data);
      });
      channel.bind(Socket.ROOM_UPDATED, async (data) => {
        await RoomSocketHandler.roomUpdate(this.$store, data.data);
      });
      channel.bind(Socket.CONSOLE_MESSAGE, async (data) => {
        this.onNewConsoleMessage(
          data.data.sound,
          data.data.message,
          data.data.fromUser
        );
      });
      channel.bind(Socket.SPACE_USER_UPDATE_POSITION, async (data) => {
        const localUser = this.$store.getters["UserStore/getSpaceUser"];
        if (data.data.id == localUser.superSpaceUser.user.id) {
          return;
        }
        const value = {
          id: data.data.id,
          destination: data.data.position,
        };
        this.$store?.dispatch("GraphicClientStore/updateUserPosition", value);
      });
      channel.bind(Socket.SPACE_EMOJI_REACTION, async (data) => {
        await SpaceSocketHandler.spaceEmojiReaction(this.$store, data);
      });
      channel.bind(Socket.REFRESH_BROWSER, async () => {
        window.location.reload();
      });
      channel.bind(Socket.POSTER_UPDATED, async (data) => {
        await SpaceSocketHandler.posterUpdate(this.$store, data);
      });
      channel.bind(Socket.POSTER_IMAGE_UPDATED, async (data) => {
        await SpaceSocketHandler.posterImageUpdate(this.$store, data);
      });
      channel.bind(Socket.SPACE_USER_UPDATE_POSITION_BY_ROOM, async (data) => {
        const value = {
          userId: data.data.id,
          roomId: data.data.roomPosition,
        };
        this.$store?.dispatch(
          "GraphicClientStore/updateUserPositionByRoom",
          value
        );
      });
      channel.bind(
        Socket.POWERUPS_ADMIN_NOTIFICATIONS_VACATION,
        async (data) => {
          if (this.isAdmin() || this.isSuperSpaceAdmin()) {
            const powerUpsNotifications =
              this.$store.getters["SidebarStore/getPowerUpsNotifications"];
            this.$store.dispatch(
              "SidebarStore/setPowerUpsNotificationsAction",
              powerUpsNotifications + 1
            );
          }
        }
      );
      channel.bind(Socket.POWERUPS_PHOTO_ALBUM_NOTIFICATIONS, async (data) => {
        const powerUpsNotifications =
          this.$store.getters["SidebarStore/getPowerUpsNotifications"];
        this.$store.dispatch(
          "SidebarStore/setPowerUpsNotificationsAction",
          powerUpsNotifications + 1
        );
      });
      channel.bind(Socket.POWERUPS_POLLS_NOTIFICATIONS, async (data) => {
        const powerUpsNotifications =
          this.$store.getters["SidebarStore/getPowerUpsNotifications"];
        this.$store.dispatch(
          "SidebarStore/setPowerUpsNotificationsAction",
          powerUpsNotifications + 1
        );
      });
      channel.bind(Socket.ASSASSIN_GAME_STARTED, async (data) => {
        await SpaceSocketHandler.Started(this.$store, data.data);
      });
      channel.bind(Socket.ASSASSIN_GAME_FINISHED, async (data) => {
        this.$store.dispatch("SystemStore/setGameKillerModalAction", false);
        this.$store.dispatch("SystemStore/setGameKillerInfoModalAction", false);
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", null);
        GraphicClient.sendMessage(
          MessageType.USER,
          UserSendMessageType.GAME_KILLER_FINISH);
      });
      channel.bind(Socket.ASSASSIN_GAME_PLAYER_KILLED, async (data) => {
        if (data.data.isAssassinGameActivated)
          this.timer = setTimeout(() => {
            this.$store.dispatch("GameKillerStore/setGameKillerLastDeadAction", data.data.spaceUserKilledName);
            const audio = new Audio(require("@/assets/audio/woman.mp3"));
            if (audio) audio.play();
          }, 300000);
        let spaceUser = this.$store.getters["UserStore/getSpaceUser"];
        if (spaceUser.id == data.data.spaceUserKilledId) {
          spaceUser.isDeadInAssassinGame = true;
          this.$store.dispatch("UserStore/setSpaceUserLocal", spaceUser);
        }
        let killer = this.$store.getters["UserStore/getGameKiller"];
        if (killer) {
          this.$store.dispatch("GameKillerStore/setGameKillerTimerKillerAction", data.data.timeToKill);
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.YOU_KILL,
            false
          );
        }
        GraphicClient.sendMessage(
          MessageType.USER,
          UserSendMessageType.UPDATE_DEAD, data.data.spaceUserKilledId);

        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
      });
      channel.bind(Socket.ASSASSIN_GAME_ASSASSIN_WON, async (data) => {
        if (this.timer)
          clearInterval(this.timer);
        this.$store.dispatch("SystemStore/setGameKillerModalAction", false);
        let space = this.$store.getters["SpaceStore/getSpace"];
        space.isAssassinGameActivated = false;
        this.$store.dispatch("SpaceStore/setSpaceAction", space);
        //this.$store.dispatch("SystemStore/setGameKillerInfoModalAction", false);
        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
        GraphicClient.sendMessage(
          MessageType.USER,
          UserSendMessageType.GAME_KILLER_FINISH);
        setTimeout(() => {
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.UPDATE_CROWN, newData.data.spaceUserWinnerId);
        }, 1000);
        this.$store.dispatch("SystemStore/setGameKillerWinnerAction", true);
        this.$store.dispatch("SystemStore/setGameKillerVoteAndWinnerModalAction", true);
      });
      channel.bind(Socket.ASSASSIN_GAME_PLAYER_REPORTED, async (data) => {
        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
        this.$store.dispatch("GameKillerStore/setGameKillerTimerVoteAction", newData.data.timeToFinishPoll);
        this.$store.dispatch("GameKillerStore/setGameKillerTimerReportAction", newData.data.timeToReport);
        let killer = this.$store.getters["UserStore/getGameKiller"];
        if (killer) {
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.YOU_KILL, false);
        }
      });
      channel.bind(Socket.ASSASSIN_GAME_PLAYER_VOTED, async (data) => {
        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
      });
      channel.bind(Socket.ASSASSIN_GAME_POLL_CLOSED, async (data) => {
        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
        let spaceUser = this.$store.getters["UserStore/getSpaceUser"];
        if (spaceUser.id == newData.data.spaceUserVotedToKillId) {
          spaceUser.isDeadInAssassinGame = true;
          this.$store.dispatch("UserStore/setSpaceUserLocal", spaceUser);
        }
        this.$store.dispatch("GameKillerStore/setGameKillerUserVotedAction", false);
        if (newData.data.spaceUserVotedToKillId && newData.data.spaceUserVotedToKillName) {
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.UPDATE_DEAD, newData.data.spaceUserVotedToKillId);
        }
        this.$store.dispatch("SystemStore/setGameKillerVoteAndWinnerModalAction", true);
        let killer = this.$store.getters["UserStore/getGameKiller"];
        if (killer) {
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.YOU_KILL,
            true
          );
        }
      });
      channel.bind(Socket.ASSASSIN_GAME_PLAYERS_WON, async (data) => {
        if (this.timer)
          clearInterval(this.timer);
        this.$store.dispatch("SystemStore/setGameKillerModalAction", false);
        let gameData = this.$store.getters["GameKillerStore/getGameKillerData"];
        let newData = data;
        newData.data.textLog.spanish = gameData.textLog.spanish + " \n" + newData.data.textLog.spanish;
        newData.data.textLog.english = gameData.textLog.english + " \n" + newData.data.textLog.english;
        newData.data.textLog.french = gameData.textLog.french + " \n" + newData.data.textLog.french;
        newData.data.textLog.catalan = gameData.textLog.catalan + " \n" + newData.data.textLog.catalan;
        this.$store.dispatch("GameKillerStore/setGameKillerDataAction", newData.data);
        let space = this.$store.getters["SpaceStore/getSpace"];
        space.isAssassinGameActivated = false;
        this.$store.dispatch("SpaceStore/setSpaceAction", space);
        //this.$store.dispatch("SystemStore/setGameKillerInfoModalAction", false);
        GraphicClient.sendMessage(
          MessageType.USER,
          UserSendMessageType.GAME_KILLER_FINISH);
        setTimeout(() => {
          GraphicClient.sendMessage(
            MessageType.USER,
            UserSendMessageType.UPDATE_CROWN, newData.data.spaceUserWinnerId);
        }, 1000);
        this.$store.dispatch("SystemStore/setGameKillerWinnerAction", false);
        this.$store.dispatch("SystemStore/setGameKillerVoteAndWinnerModalAction", true);
      });
    }
  }
  async subscribeToUserPrivateChannel(): Promise<void> {
    if (
      this.spaceUser == undefined ||
      this.spaceUser.superSpaceUser.user == undefined ||
      this.spaceUser.superSpaceUser.user.id == undefined
    ) {
      console.log("RETRY subscribeToPrivate");
      setTimeout(() => {
        this.subscribeToUserPrivateChannel();
      }, 10000);
      return;
    }
    const pusher = this.$store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(
      PUSHER_CHANNEL.PRIVATE_USER + this.spaceUser.superSpaceUser.user.id
    );
    if (channelExist && channelExist.subscribe) return;
    if (pusher.connection.state === "connected") {
      let channel = pusher.subscribe(
        PUSHER_CHANNEL.PRIVATE_USER + this.spaceUser.superSpaceUser.user.id
      );
      channel.bind("pusher:subscription_error", async (status) => {
        console.log(
          "subscribe to user private channel error ",
          moment().toString(), " ", status
        );
        if (status == 401) {
          channel.subscriptionPending = false;
          const authService = new AuthService();
          await authService.refreshToken(true).then(async () => {
            pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
              "token"
            )}`;
            await this.$store.dispatch("SystemStore/setPusherAction", pusher);
            channel = pusher.subscribe(
              PUSHER_CHANNEL.PRIVATE_USER +
              this.spaceUser.superSpaceUser.user.id
            );
          });
        } /* else {
          if (localStorage.getItem("WEB_CLIENT_LOG_ACTIVATED") == "1")
            this.$store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: "Pusher subscribe to user private channel error " + status,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });
        }*/
      });
      channel.bind(Socket.ASSASSIN_GAME_YOU_ARE_ASSASSIN, async (data) => {
        this.$store.dispatch("UserStore/setGameKillerAction", true);
        GraphicClient.sendMessage(
          MessageType.USER,
          UserSendMessageType.UPDATE_ASSASSIN, true);
      });
      channel.bind(Socket.ASSASSIN_YOU_HAVE_PENALTY_AND_CANNOT_REPORT, async (data) => {
        this.$store.dispatch(
          "GameKillerStore/setGameKillerTimerBanAction",
          data.data.timeToReportPenalty
        );
      });
      channel.bind(Socket.ROOM_JOIN_MEETING, async (data) => {
        await RoomSocketHandler.roomJoinMeeting(this.$store, data.data);
      });
      channel.bind(Socket.USER_RECEIVING_MEET, async (data) => {
        await UserSocketHandler.userReceivingMeet(
          this.$store,
          data.data,
          this.stopLoader,
          this.startLoader,
          this.onWarning
        );
      });
      channel.bind(Socket.ROOM_JOIN_CHAT, async (data) => {
        if (process.env.VUE_APP_ENVIRONMENT !== "PRO")
          console.log("ROOM_JOIN_CHAT", data);
      });
      channel.bind(Socket.CONSOLE_MESSAGE, async (data) => {
        if (data.data.type === "SPACE_MESSAGE")
          this.onNewConsoleMessage(
            data.data.sound,
            data.data.message,
            data.data.fromUser
          );
        else if (data.data.type === "THROW_PAPER_PLANE") {
          const localUser = this.$store.getters["UserStore/getSpaceUser"];
          await this.$store.dispatch(
            "GraphicClientStore/sendPaperPlaneAction",
            {
              sendId: data.data.fromUser.id,
              receiveId: localUser.superSpaceUser.user.id,
            }
          );
          this.onNewPaperPlane(
            data.data.message,
            data.data.fromUser,
            localUser.superSpaceUser.user.availableStatus
          );
        } else if (data.data.type === "THROW_PAPER_PLANE_APP") {
          const localUser = this.$store.getters["UserStore/getSpaceUser"];
          await this.$store.dispatch(
            "GraphicClientStore/sendPaperPlaneAction",
            {
              sendId: data.data.fromUser.id,
              receiveId: localUser.superSpaceUser.user.id,
            }
          );
          this.onNewPaperPlane(
            i18n
              .t("notifications.newPaperPlaneApp", {
                name: data.data.fromUser.name,
                lastName: data.data.fromUser.lastName,
                room: data.data.message,
              })
              .toString(),
            data.data.fromUser,
            localUser.superSpaceUser.user.availableStatus
          );
        } else if (data.data.type === "CONGRATULATE_BIRTHDAY") {
          const localUser = this.$store.getters["UserStore/getSpaceUser"];
          await this.$store.dispatch(
            "GraphicClientStore/sendBirthdayCokeAction",
            {
              sendId: data.data.fromUser.id,
              receiveId: localUser.superSpaceUser.user.id,
            }
          );
          this.congratulateBirthday(
            data.data.message,
            data.data.fromUser,
            localUser.superSpaceUser.user.availableStatus
          );
        }
      });
      channel.bind(Socket.SPACE_USER_TECHNICAL_INFO_REQUESTED, async (data) => {
        await SpaceUserSocketHandler.spaceUserTechnicalInfoRequested(
          this.$store,
          data.data
        );
      });
      channel.bind(Socket.SPACE_USER_TECHNICAL_INFO_RECEIVED, async (data) => {
        await SpaceUserSocketHandler.spaceUserTechnicalInfoReceived(
          this.$store,
          data.data
        );
      });
      channel.bind(Socket.ROOM_SPEED_NETWORKING_WAITING, async (data) => {
        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingTimeAction",
          data.data.nextSpeedNetworkingCycleTime
        );
        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingMeetingAction",
          false
        );
        const roomId = this.$store.getters["MeetingStore/getRoomId"];
        if (roomId <= 0 || roomId !== data.data.roomId) return;
        const meeting = this.$store.getters["MeetingStore/getMeeting"];
        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingMeetingAction",
          true
        );
        await this.$store.dispatch(
          "SpeedNetworkingStore/setUserInMeetingAction",
          null
        );
        if (meeting) {
          await this.$store.dispatch("SidebarStore/setOpenCall", false);
          this.$store.dispatch("ChatStore/setMinimizeChatAction", false);
        }
      });
      channel.bind(Socket.ROOM_SPEED_NETWORKING_MEET, async (data) => {
        const meeting = this.$store.getters["MeetingStore/getMeeting"];
        const roomId = this.$store.getters["MeetingStore/getRoomId"];
        await this.$store.dispatch(
          "SpeedNetworkingStore/setMeetingTimeAction",
          data.data.nextSpeedNetworkingCycleTime
        );
        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingTimeAction",
          null
        );
        if (roomId <= 0 || roomId !== data.data.roomId) return;
        if (meeting) {
          await this.$store.dispatch("SidebarStore/setOpenCall", false);
          await this.$store.dispatch("ChatStore/setMinimizeChatAction", false);
          await this.$store.dispatch(
            "SpeedNetworkingStore/setUserInMeetingAction",
            null
          );
          await this.$store.dispatch("MeetingStore/closeMeetingSession");
        }

        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingMeetingAction",
          true
        );
        await this.startLoader(this.$store, CANCELLATION_TIME.CANCEL_CALL_30);
        await this.$store.dispatch(
          "MeetingStore/setMeetingDataAction",
          data.data
        );
        await this.$store.dispatch("SidebarStore/setOpenCall", true);
        await this.$store.dispatch(
          "ChatStore/setMinimizeChatAction",
          !this.$store?.getters["UserStore/getConfig"].chat
            .openRoomChatByDefault
        );
        await this.$store.dispatch(
          "MeetingStore/setMeetingTypeAction",
          MEETING_TYPE.SPEED_NETWORKING
        );
        await this.$store.dispatch(
          "SpeedNetworkingStore/setWaitingMeetingAction",
          false
        );
        await this.$store.dispatch(
          "SpeedNetworkingStore/setUserInMeetingAction",
          data.data.fromSpaceUser
        );

        await this.$store.dispatch("SidebarStore/setOpenChatAction", false);
        await this.$store.dispatch(
          "ChatStore/setUserChatAction",
          data.data.fromSpaceUser
        );
        setTimeout(() => {
          this.$store.dispatch("GraphicClientStore/resizeGraphicClientAction");
        }, 20);
      });
      channel.bind(Socket.REFRESH_BROWSER, async () => {
        window.location.reload();
      });
      channel.bind(Socket.POWERUPS_DAILY_TASKS_NOTIFICATIONS, async (data) => {
        const space = this.$store.getters["SpaceStore/getSpace"];

        if (data.data.spaceId && data.data.SpaceId == space.id) {
          const powerUpsNotifications =
            this.$store.getters["SidebarStore/getPowerUpsNotifications"];
          this.$store.dispatch(
            "SidebarStore/setPowerUpsNotificationsAction",
            powerUpsNotifications + 1
          );
        }
      });
      channel.bind(
        Socket.POWERUPS_DAILY_TASKS_DONE_NOTIFICATIONS,
        async (data) => {
          const space = this.$store.getters["SpaceStore/getSpace"];

          if (data.data.spaceId && data.data.SpaceId == space.id) {
            const powerUpsNotifications =
              this.$store.getters["SidebarStore/getPowerUpsNotifications"];
            this.$store.dispatch(
              "SidebarStore/setPowerUpsNotificationsAction",
              powerUpsNotifications + 1
            );
          }
        }
      );
      channel.bind(
        Socket.POWERUPS_VACATION_STATUS_NOTIFICATION,
        async (data) => {
          const space = this.$store.getters["SpaceStore/getSpace"];

          if (data.data.spaceId && data.data.SpaceId == space.id) {
            const powerUpsNotifications =
              this.$store.getters["SidebarStore/getPowerUpsNotifications"];
            this.$store.dispatch(
              "SidebarStore/setPowerUpsNotificationsAction",
              powerUpsNotifications + 1
            );
          }
        }
      );
      channel.bind(
        Socket.POWERUPS_SEND_ONBOARDING_USER_NOTIFICATION,
        async (data) => {
          const space = this.$store.getters["SpaceStore/getSpace"];

          if (data.data.spaceId && data.data.SpaceId == space.id) {
            const powerUpsNotifications =
              this.$store.getters["SidebarStore/getPowerUpsNotifications"];
            this.$store.dispatch(
              "SidebarStore/setPowerUpsNotificationsAction",
              powerUpsNotifications + 1
            );
          }
        }
      );
      channel.bind(
        Socket.POWERUPS_READ_ONBOARDING_USER_NOTIFICATION,
        async (data) => {
          const space = this.$store.getters["SpaceStore/getSpace"];

          if (data.data.spaceId && data.data.SpaceId == space.id) {
            const powerUpsNotifications =
              this.$store.getters["SidebarStore/getPowerUpsNotifications"];
            this.$store.dispatch(
              "SidebarStore/setPowerUpsNotificationsAction",
              powerUpsNotifications + 1
            );
          }
        }
      );
    }
  }
  async getOnlyUserOnline(): Promise<void> {
    await this.$store.dispatch("SpaceStore/setUsersInSpaceAction", []);
    await this.$store.dispatch("SpaceStore/setUsersInSpacePageAction", 1);
    await this.$store.dispatch("SpaceStore/setLastPageUserAction", 2);
    const space = this.$store.getters["SpaceStore/getSpace"];
    await this.$store.dispatch("SpaceStore/getSpaceUsersAction", {
      superSpaceId: space.superSpace.id,
      callback: () => null,
    });
    await this.$store.dispatch("SpaceStore/getOnlyUsersOnlineAction", {
      spaceId: space.id,
      callback: async (usersOnline) => {
        await this.$store.dispatch(
          "GraphicClientStore/spaceReadyAction",
          usersOnline
        );
      },
      callbackError: (error) => {
        console.log("getOnlyUsersOnlineAction ", error);
      },
    });
  }
  async checkMeeting(): Promise<void> {
    const attendee = this.$store.getters["MeetingStore/getAttendee"];
    const roomId = this.$store.getters["MeetingStore/getRoomId"];
    if (!attendee && roomId)
      await this.$store?.dispatch("MeetingStore/leaveMeetingAction", {
        roomId: roomId,
        callback: async (response) => {
          await this.$store?.dispatch("SidebarStore/setOpenCall", false);
          await this.$store.dispatch("ChatStore/setMinimizeChatAction", false);
          await this.$store?.dispatch("ChatStore/leaveRoomChatAction", roomId);
        },
        callbackError: (error) => {
          console.log("Error checkMeeting", error);
        },
      });
  }
  async subscribePrivateRoomChannel(idRoom, store): Promise<void> {
    const pusher = store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(
      PUSHER_CHANNEL.PRIVATE_ROOM + idRoom
    );
    if (channelExist && channelExist.subscribe) return;
    if (pusher.connection.state === "connected") {
      let channel = pusher.subscribe(
        PUSHER_CHANNEL.PRIVATE_ROOM + idRoom
      );
      channel.bind("pusher:subscription_error", async (status) => {
        console.log(
          "subscribe private channel error ",
          moment().toString(), " ", status
        );
        if (status == 401) {
          channel.subscriptionPending = false;
          const authService = new AuthService();
          await authService.refreshToken(true).then(async () => {
            pusher.config.auth.headers.Authorization = `Bearer ${localStorage.getItem(
              "token"
            )}`;
            await store.dispatch("SystemStore/setPusherAction", pusher);
            channel = pusher.subscribe(
              PUSHER_CHANNEL.PRIVATE_ROOM + idRoom
            );
          });
        } /* else {
          if (localStorage.getItem("WEB_CLIENT_LOG_ACTIVATED") == "1")
            store.dispatch("LogsStore/sendGraphicClientErrorAction", {
              data: {
                type: "WEB_CLIENT",
                errorMessage: "Pusher subscribe to user private channel error " + status,
                userMessage: ""
              },
              callback: () => { },
              callbackError: () => { },
            });
        }*/
      });
      channel.bind(Socket.POLL_CREATED, async (data) => {
        await RoomSocketHandler.pollCreated(store, data);
      });
      channel.bind(Socket.POLL_VOTED, async (data) => {
        await RoomSocketHandler.pollVoted(store, data);
      });
      channel.bind(Socket.POLL_CLOSED, async (data) => {
        await RoomSocketHandler.pollClosed(store, null);
      });
      channel.bind(Socket.ROOM_RESERVE_STARTED, async (data) => {
        const meetingType = store.getters["MeetingStore/getMeetingType"];
        await store.dispatch("SidebarStore/setOpenChatAction", false);
        if (
          meetingType !== MEETING_TYPE.ONE_TO_ONE &&
          meetingType !== MEETING_TYPE.SPEED_NETWORKING
        )
          await store.dispatch("ChatStore/setUserChatAction", null);
        await store.dispatch("ChatStore/setMessagesAction", []);
        this.onSuccess(
          i18n.t("notifications.titles.information"),
          i18n.t("notifications.descriptions.reserveRoomStart"),
          "none"
        );
      });
      channel.bind(Socket.ROOM_RESERVE_FINISHED, async (data) => {
        const space = store.getters["SpaceStore/getSpace"] as SpaceInterface;
        const roomId = store.getters["MeetingStore/getRoomId"];
        let room = space.scenario.rooms.filter(
          (a) => a.id === roomId
        )[0];
        room.reservationStatus = data.data.reservationStatus;
        room.hasPin = data.data.hasPin;
        store.dispatch("SpaceStore/setSpaceAction", space);
        this.onSuccess(
          i18n.t("notifications.titles.information"),
          i18n.t("notifications.descriptions.reserveRoomFinish"),
          "none"
        );
      });
    }
  }
  async unSubscribePrivateRoomChannel(idRoom, store): Promise<void> {
    const pusher = store.getters["SystemStore/getPusher"];
    const channelExist = pusher.channel(
      PUSHER_CHANNEL.PRIVATE_ROOM + idRoom
    );
    if (channelExist && channelExist.subscribe) {
      if (pusher.connection.state === "connected") {
        let channel = pusher.unsubscribe(
          PUSHER_CHANNEL.PRIVATE_ROOM + idRoom
        );
        await RoomSocketHandler.pollClosed(store, null);
      }
    }
  }

  connectGenetalChat(): boolean {
    if (this.$store.getters["SpaceStore/getSpace"].isChatActive && this.activeOptionPlan(PLAN.GENERAL_CHAT))
      return true;
    return false;
  }
}
